import React, { useContext, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import SideBar from '../../uiParts/SideBar';
import {
  gray10,
  gray100,
  gray20,
  gray70,
  gray90,
  primaryButtonBackgroundColor,
  primaryButtonOutlinedColor,
  primaryColor
} from '../../../constants/Colors';
import { userType } from '../../../constants/Enum';
import { sideBarWidth } from '../../../constants/Location';
import { AuthContext } from '../../../contexts/auth';
import { getOperatorMaterialList } from '../../../api/db/material';
import { common } from '../../../modules/error';
import { response } from '../../../api/db';
import { AxiosError } from 'axios';
import CustomLoading from '../../uiParts/CustomLoading';
import { sentryLog } from '../../../config/sentry';

const styles = {
  wrapper: {
    '@media screen and (max-width:960px)': {
      margin: '24px 16px'
    },
    margin: '32px'
  },
  headerContainer: {
    mb: '24px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px',
      pr: '8px'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: 500
  },
  settingName: {
    '@media screen and (max-width:600px)': {
      fontSize: '16px',
      pl: '8px'
    },
    fontSize: '24px',
    color: gray100,
    pl: '16px'
  },
  savedCo2Button: {
    ...primaryButtonOutlinedColor,
    p: '10px 16px',
    borderRadius: '4px',
    fontSize: '16px',
    hight: '44px',
    '@media screen and (max-width:960px)': {
      mt: '24px'
    }
  },
  saveButton: {
    ...primaryButtonBackgroundColor,
    ml: '16px',
    p: '10px 16px',
    borderRadius: '4px',
    fontSize: '16px',
    hight: '44px'
  },
  spButtonContainer: {
    width: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.80)',
    p: '12px 16px',
    position: 'fixed',
    bottom: '0',
    left: '0',
    zIndex: 10,
    '@media screen and (min-width:961px)': {
      display: 'none'
    }
  },
  saveButtonSp: {
    ...primaryButtonBackgroundColor,
    p: '10px 16px',
    borderRadius: '4px',
    fontSize: '16px',
    width: '100%',
    hight: '44px'
  }
};

const tableStyles = {
  button: {
    border: '1px solid' + primaryColor,
    color: primaryColor,
    width: '100%',
    minWidth: '0',
    borderRadius: '4px',
    fontSize: '16px',
    p: '2px 8px',
    hight: '44px'
  },
  tableContainer: {
    border: '1px solid' + gray70,
    borderBottom: 'none'
  },
  headCell: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    },
    fontSize: '16px',
    fontWeight: 500,
    color: gray90,
    p: '10px 16px',
    borderRight: '1px solid' + gray70
  },
  subHeader: {
    '@media screen and (max-width:600px)': {
      fontSize: '12px',
      p: '7px 16px'
    },
    backgroundColor: gray20,
    fontSize: '14px',
    color: gray100,
    p: '10px 32px',
    borderBottom: '1px solid' + gray70
  },
  buttonCell: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    },
    fontSize: '16px',
    color: gray100,
    p: '8px',
    width: '45px',
    backgroundColor: gray10,
    borderRight: '1px solid' + gray70,
    borderBottom: '1px solid' + gray70,
    textAlign: 'center'
  },
  cell: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    },
    fontSize: '16px',
    color: gray100,
    p: '10px 16px',
    backgroundColor: gray10,
    borderRight: '1px solid' + gray70,
    wordBreak: 'break-all'
  },
  newMark: {
    '@media screen and (max-width:600px)': {
      width: '8px',
      height: '8px',
      mr: '4px'
    },
    width: '10px',
    height: '10px',
    mr: '7px'
  }
};

const Co2Standard = (): React.ReactElement => {
  // 画面のアクセス制御
  const navigate = useNavigate();
  const { isLoggedIn, setSnackbarOpen, setErrorText, setInfoText } =
    useContext(AuthContext);
  const currentUserType = Number(localStorage.getItem('user_type'));
  const [rows, setRows] = useState<
    { categoryName: string; rowData: RowData[] }[]
  >([{ categoryName: '', rowData: [] }]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    void (async () => {
      setLoading(true);

      try {
        // 未ログインの場合はログイン画面に飛ばす
        if (!isLoggedIn) return navigate('/auth/login');
        // 事務局画面で事務局ユーザーではない場合は、ホームに飛ばす
        if (currentUserType !== userType.admin) return navigate('/home');

        const materialRes = await getOperatorMaterialList();
        // カテゴリごとにオブジェクトを作成
        const categories = materialRes.data.reduce(
          (
            acc: {
              [key: string]: { rowData: RowData[]; categorySort: number };
            },
            curr: {
              id: number;
              category_name: string;
              name: string;
              value: string;
              co2_unit: string;
              cost_unit: string;
              remark: string;
              price_condition: string;
              calculation_specification: string;
              sort: string;
              category_id: number;
              category_sort: number;
            }
          ) => {
            if (!acc[curr.category_name]) {
              acc[curr.category_name] = {
                rowData: [],
                categorySort: curr.category_sort
              };
            }
            acc[curr.category_name].rowData.push({
              id: curr.id,
              name: curr.name,
              standard: curr.value,
              co2Unit: curr.co2_unit,
              costUnit: curr.cost_unit || '',
              remark: curr.remark || '',
              priceCondition: curr.price_condition,
              calculation: curr.calculation_specification,
              sortNo: curr.sort,
              categoryId: curr.category_id
            });
            return acc;
          },
          {}
        );

        // 上記オブジェクトを配列に変換し、category_sort順にソート
        const data: { categoryName: string; rowData: RowData[] }[] =
          Object.keys(categories)
            .map((key: string) => ({
              categoryName: key,
              rowData: categories[key].rowData,
              categorySort: categories[key].categorySort
            }))
            .sort((a, b) => a.categorySort - b.categorySort);

        // stateを更新
        setRows(data);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(errorResponse.data.message);
            setSnackbarOpen(true);
            setInfoText('');
          }
        } else {
          if (setErrorText && setSnackbarOpen && setInfoText) {
            setErrorText(common);
            setSnackbarOpen(true);
            setInfoText('');
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [isLoggedIn, currentUserType]);

  return (
    <Grid width="100%">
      <CustomLoading loading={loading} />
      <SideBar />
      <Grid
        sx={{
          '@media screen and (min-width:961px)': {
            ml: sideBarWidth
          },
          minWidth: '900px',
          overflowX: 'scroll',
          mb: '200px'
        }}
      >
        <Box sx={styles.wrapper}>
          <Grid
            container
            justifyContent="space-between"
            sx={styles.headerContainer}
          >
            <Grid xs={6} item>
              <Grid container alignItems="center">
                <Typography sx={styles.title}>部材管理</Typography>
              </Grid>
            </Grid>
            <Grid xs={6} item container justifyContent="flex-end">
              <Button
                sx={styles.saveButton}
                onClick={() => navigate('/operator/materials/new')}
              >
                <AddIcon sx={{ mr: '8px' }} />
                新規登録
              </Button>
            </Grid>
          </Grid>
          <Grid sx={tableStyles.tableContainer}>
            <Grid container sx={{ borderBottom: '1px solid' + gray70 }}>
              <Grid item sx={[tableStyles.headCell, { width: '45px' }]}>
                {'\u3000'}
              </Grid>
              <Grid item sx={tableStyles.headCell} xs={1.75}>
                <Typography>部材名</Typography>
              </Grid>
              <Grid item sx={tableStyles.headCell} xs={1.15}>
                <Typography>CO2基準値</Typography>
              </Grid>
              <Grid item sx={tableStyles.headCell} xs={1.15}>
                <Typography>CO2単位</Typography>
              </Grid>
              <Grid item sx={tableStyles.headCell} xs={1.15}>
                <Typography>単価単位</Typography>
              </Grid>
              <Grid item sx={tableStyles.headCell} xs={1.75}>
                <Typography>基準CO2算定仕様</Typography>
              </Grid>
              <Grid item sx={tableStyles.headCell} xs={1.75}>
                <Typography>単価算定条件</Typography>
              </Grid>
              <Grid item sx={tableStyles.headCell} xs={1.3}>
                <Typography>備考</Typography>
              </Grid>
              <Grid
                item
                sx={tableStyles.headCell}
                xs={1.1}
                style={{ borderRight: 'none' }}
              >
                <Typography>ソート番号</Typography>
              </Grid>
            </Grid>
            {rows.map((group, index) => (
              <Grid container key={index}>
                <Grid item xs={12} sx={tableStyles.subHeader}>
                  <Typography>{group.categoryName}</Typography>
                </Grid>
                {group.rowData.map((row, index) => (
                  <TableRow {...row} key={index} />
                ))}
              </Grid>
            ))}
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Co2Standard;

type RowData = {
  id: number;
  name: string;
  standard: string;
  co2Unit: string;
  costUnit: string;
  remark: string;
  priceCondition: string;
  calculation: string;
  sortNo: string;
  categoryId: number;
};

const TableRow = (rowData: RowData): React.ReactElement => {
  const navigate = useNavigate();

  const formatNumber = (num: number): number => {
    if (num >= 100) {
      return Math.round(num);
    } else {
      return Math.round(num * 1000) / 1000;
    }
  };

  return (
    <Grid
      container
      sx={{ backgroundColor: gray10, borderBottom: '1px solid' + gray70 }}
    >
      <Grid item sx={tableStyles.buttonCell}>
        <Button
          onClick={() =>
            navigate(`/operator/materials/edit/${rowData.id}`, {
              state: { data: rowData }
            })
          }
          sx={tableStyles.button}
        >
          <EditOutlinedIcon />
        </Button>
      </Grid>
      <Grid item sx={tableStyles.cell} xs={1.75}>
        <Box>{rowData.name}</Box>
      </Grid>
      <Grid item sx={tableStyles.cell} xs={1.15}>
        <Box>{formatNumber(Number(rowData.standard))}</Box>
      </Grid>
      <Grid item sx={tableStyles.cell} xs={1.15}>
        <Box>{rowData.co2Unit}</Box>
      </Grid>
      <Grid item sx={tableStyles.cell} xs={1.15}>
        <Box>{rowData.costUnit}</Box>
      </Grid>
      <Grid item sx={tableStyles.cell} xs={1.75}>
        <Box>{rowData.calculation}</Box>
      </Grid>
      <Grid item sx={tableStyles.cell} xs={1.75}>
        <Box>{rowData.priceCondition}</Box>
      </Grid>
      <Grid item sx={tableStyles.cell} xs={1.3}>
        <Box>{rowData.remark}</Box>
      </Grid>
      <Grid item sx={tableStyles.cell} xs={1.1} style={{ borderRight: 'none' }}>
        <Box>{rowData.sortNo}</Box>
      </Grid>
    </Grid>
  );
};
